<template>
  <fieldset :class="_fs_classes" :aria-labelledby="hint ? hintId : undefined">
    <cat-label
      :label-id="id + '_lbl'"
      :fieldset="true"
      :label="label"
      :optional="!mandatory"
      :errors="errors"
      :readonly="readonly"
      :inline="help && !!help.url"
    ></cat-label>

    <cat-help-trigger
      v-if="help && help.url"
      :source-id="id"
      :url="help.url"
      :title="label"
    />
    <div v-if="help && help.url" class="clearfix"></div>
    <cat-hint :id="hintId" :text="hint" />

    <slot></slot>

    <cat-help
      v-if="help && help.text"
      :id="id + '_help'"
      :heading="help.title"
      :text="help.text"
      :aria-describedby="label && id ? id + '_lbl' : null"
    />

    <div class="checkbox-list">
      <div v-for="(item, idx) in items" :key="idx" class="checkbox-item">
        <cat-check-box
          :id="id + '-' + idx"
          :label="item.label"
          :value="item.value"
          :selected="item.selected"
          :readonly="readonly"
          :disabled="readonly"
          :display-required="false"
          :display-optional="false"
          :ariaDescribedby="item.hint ? id + '_' + idx + '_hint' : null"
          @ui-change="_onChange"
        />

        <cat-hint
          v-if="item.hint"
          :id="id + '_' + idx + '_hint'"
          :text="item.hint"
        />
        <cat-help
          v-if="item.help"
          :id="id + '_' + idx + '_help'"
          :text="item.help"
          :heading="item.helpTitle"
          :aria-describedby="label && id ? id + '_lbl' : null"
        />
      </div>
    </div>

    <slot name="after-input"></slot>
    <cat-errors :errors="errors" />
  </fieldset>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatHelp from "@/components/utilities/help.vue";
import CatLabel from "@/components/utilities/cat-label.vue";
import CatHint from "@/components/utilities/hint.vue";
import CatHelpTrigger from "@/components/catPageTemplate/catHelpTrigger.vue";
import CatCheckBox from "@/components/catCheckBox/catCheckBox.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    return {
      resolveTxt,
      displayParams
    };
  },
  name: "CatCheckboxGroup",
  components: {
    CatErrors,
    CatHelp,
    CatLabel,
    CatHelpTrigger,
    CatHint,
    CatCheckBox
  },
  props: {
    id: {
      type: [Number, String],
      description: "Checkbox group ID."
    },
    errors: {
      type: Array,
      description: "List of errors."
    },
    items: {
      type: Array,
      description:
        "List of item objects with the following fields - value, label, hint, help."
    },
    mandatory: {
      type: [Boolean, String],
      description: "Indicates if field is mandatory."
    },
    label: {
      type: String,
      description: "Checkbox group label."
    },
    hint: {
      type: String,
      description: "Text displayed as a hint."
    },
    readonly: {
      type: Boolean,
      description: "Indicates if field is read only."
    },
    help: {
      type: Object,
      description: "Passes on a help object."
    },
    horizontal: {
      type: Boolean,
      default: false,
      description: "Forces the checkbox to display horizontally."
    },
    inlineLabel: {
      type: Boolean,
      default: false,
      description: "Displays the attribute label inline with the checkboxes."
    }
  },
  computed: {
    _fs_classes: function () {
      return {
        fieldset: true,
        "cat-checkbox-group": true,
        invalid: this.errors && this.errors.length > 0,
        required: this.mandatory,
        optional: !this.mandatory,
        horizontal: this.horizontal,
        "readonly-list": this.readonly,
        "inline-label": this.inlineLabel
      };
    },
    hintId: function () {
      return this.id + "-fs-hint";
    }
  },

  methods: {
    _onChange: function (evt) {
      this.$emit("ui-change", {
        event: evt,
        id: this.id ? this.id : null,
        value: evt.value,
        code: evt.code,
        el: evt.el
      });
    }
  }
});
</script>
